import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  VStack,
  useToast,
} from '@chakra-ui/react';
import ReactSelect from 'react-select';
import { useQuery } from '@tanstack/react-query';
import { useClinic } from '@/components/Context/ClinicaContext';
import { createRecado } from '@/services/recadoService';
import { listChatContacts } from './contactsService';

function dateToLocalDateTimeString(dateStr) {
  const d = String(dateStr || '').trim();
  if (!d) return null;
  return `${d}T00:00:00`;
}

export default function RecadoModal({ isOpen, onClose, clinicaId, onCreated }) {
  const toast = useToast();
  const { selectedClinic } = useClinic();
  const [selected, setSelected] = useState(null);

  const [texto, setTexto] = useState('');
  const [dataFinal, setDataFinal] = useState('');
  const [horario, setHorario] = useState('');
  const [saving, setSaving] = useState(false);

  // Usuários e sócios da clínica — mesma fonte usada no chat, já retorna os dois tipos
  // combinados num único formato.
  const { data: contacts } = useQuery({
    queryKey: ['lista_users_socios', selectedClinic?._id],
    queryFn: () => listChatContacts({ clinicaId: selectedClinic._id }),
    enabled: !!selectedClinic?._id,
    refetchOnWindowFocus: false,
  });

  const userOptions = useMemo(
    () => (contacts || []).map((c) => ({ value: c.id, label: c.label || c.name, userKey: c.userKey })),
    [contacts]
  );

  useEffect(() => {
    if (!isOpen) setSelected(null);
  }, [isOpen]);

  const canSave = useMemo(() => {
    return !!clinicaId && !!selected?.value && String(texto || '').trim().length > 0;
  }, [clinicaId, selected?.value, texto]);

  const handleClose = () => {
    setSelected(null);
    setTexto('');
    setDataFinal('');
    setHorario('');
    onClose?.();
  };

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      await createRecado({
        clinicaId,
        toUserKey: selected.userKey || `user:${selected.value}`,
        texto: String(texto || '').trim(),
        dataFinal: dateToLocalDateTimeString(dataFinal),
        horario: String(horario || '').trim() ? String(horario).trim() : null,
      });
      toast({
        title: 'Recado enviado',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
      onCreated?.();
      handleClose();
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('Erro ao enviar recado:', e);
      toast({
        title: 'Erro ao enviar',
        description: e?.message || 'Não foi possível enviar o recado',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="lg" isCentered closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Novo recado</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={3} align="stretch">
            <FormControl isRequired>
              <FormLabel>Usuário</FormLabel>
              <ReactSelect
                options={userOptions}
                value={selected}
                onChange={(opt) => setSelected(opt ? { value: opt.value, label: opt.label, userKey: opt.userKey } : null)}
                placeholder="Pesquisar usuário..."
                isClearable
                isDisabled={!clinicaId}
                noOptionsMessage={() => 'Nenhum usuário encontrado'}
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Recado</FormLabel>
              <Textarea
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                placeholder="Recado"
                minH="120px"
                maxLength={2000}
              />
              <Text fontSize="xs" color="gray.500" mt={1}>
                {String(texto || '').length}/2000
              </Text>
            </FormControl>

            <HStack spacing={3} wrap="wrap">
              <FormControl>
                <FormLabel>Data final</FormLabel>
                <Input type="date" value={dataFinal} onChange={(e) => setDataFinal(e.target.value)} />
              </FormControl>
              <FormControl>
                <FormLabel>Horário</FormLabel>
                <Input type="time" value={horario} onChange={(e) => setHorario(e.target.value)} />
              </FormControl>
            </HStack>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" onClick={handleClose}>
            Cancelar
          </Button>
          <Button
            colorScheme="blue"
            ml={3}
            onClick={handleSave}
            isLoading={saving}
            isDisabled={!canSave}
          >
            Enviar recado
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

